/**
 * V2 "constructor" line model.
 *
 * A daily-report `body_html` is line-delimited (`\n` per row). This parses those
 * rows into *logical lines* — a recognised report concept plus the raw row span it
 * occupies — so the per-line "circle → form" editor can present a structured form
 * and splice a regenerated row back in without disturbing the rest of the text.
 *
 * The one fully-modelled type is `food_status` (the Выкладка per-rack-type line),
 * whose exact wording mirrors the backend generator
 * (`services/plugins/periodic_reports/tasks/daily_report/sections/empty_shelf.py`):
 *   OK       →  `🟢 {rackType}: не было` (was `ОК` — renamed 2026-09, Round-2 item 3;
 *               FOOD_OK_RE/serializeFoodStatus were updated in lockstep, so this
 *               is a one-word swap, not a back-compat pair — unlike keypointBlock.ts's
 *               drastically-reshaped block format, historical `ОК` reports simply
 *               fall through to a plain `other` line instead of a `food_status` one)
 *   problem  →  `{emoji} {rackType}: по <u>{n} полке|полкам</u> средняя нехватка `
 *               `<u>{avg}%.</u> Проблемы наблюдались в течение <u>{dur}ч</u>.`
 *   + next row: `<blockquote>Ссылки на проблемные зоны: <a href>{name}</a>, …</blockquote>`
 * Everything else is an `other` line, editable as plain text via a generic form.
 */

import { matchTypedLine, type TypedFields, type TypedKind, type TypedMeta } from './typedLines';
import {
  isKeypointRow,
  keypointEmoji,
  parseKeypointBlock,
  type KeypointBlock,
} from './keypointBlock';
import {
  consumeTreeBlockRows,
  isCategoryTreeRow,
  isLongestQueueRow,
  isLongQueueRow,
  isLongQueueSecondCashierRow,
  isNoVisitorsRow,
  parseCategoryTreeFields,
  parseLongestQueueFields,
  parseLongQueueFields,
  parseNoVisitorsFields,
  type CategoryTreeFields,
  type LongestQueueFields,
  type LongQueueFields,
  type NoVisitorsFields,
} from './treeBlock';
import {
  consumeChecklistBlockRows,
  isChecklistRow,
  parseChecklistFields,
  type ChecklistFields,
} from './checklistBlock';

export type Severity = 'yellow' | 'orange' | 'red' | 'grey';

// grey = "empty" — accepted on PARSE only (older hand-edited rows carry ⚪/⚫);
// serializeFoodStatus never emits it: the circle is always derived from the
// numbers via deriveFoodSeverity (Alexey 2026-09-08: «цвета чётко считались из
// того, что они вводят»).
export const SEVERITY_EMOJI: Record<Severity, string> = {
  yellow: '🟡',
  orange: '🟠',
  red: '🔴',
  grey: '⚪',
};

const EMOJI_SEVERITY: Record<string, Severity> = {
  '🟡': 'yellow',
  '🟠': 'orange',
  '🔴': 'red',
  '⚪': 'grey',
  '⚫': 'grey',
};

export interface ProblemLink {
  name: string;
  url: string | null; // null → rendered as a plain name (BE's link-failure fallback)
}

// A rack of a given rack_type, offered in the problem-zone dropdown. `url` is the
// rack's Grafana dashboard link (base; from/to applied from the problem period), or
// null when unknown (no BE per-rack link yet → renders as plain text).
export interface RackOption {
  name: string;
  url: string | null;
}

/** Map every distinct anchor label → its href across the whole body_html. */
export function extractReportLinks(bodyHtml: string): Record<string, string> {
  const map: Record<string, string> = {};
  let m: RegExpExecArray | null;
  ANCHOR_RE.lastIndex = 0;
  while ((m = ANCHOR_RE.exec(bodyHtml)) !== null) {
    const name = m[2].replace(/<[^>]+>/g, '').trim();
    if (name && !map[name]) map[name] = m[1];
  }
  return map;
}

export interface FoodStatusFields {
  rackType: string;
  ok: boolean;
  severity: Severity;
  shelfCount: number;
  avgShortage: number; // percent
  durationHours: number;
  links: ProblemLink[];
}

export interface FoodStatusLine {
  kind: 'food_status';
  startIdx: number;
  endIdx: number; // inclusive; spans the status row (+ blockquote row if present)
  fields: FoodStatusFields;
}

// A generic recognised line: any row carrying <b>/<u> highlighted values (keypoint
// 🥖 lines, queue ⚪ lines, etc.). We don't model each Russian template — we let the
// validator edit the highlighted values while the surrounding prose stays verbatim.
export interface Highlight {
  tag: 'b' | 'u';
  inner: string; // editable value text
  label: string; // ~preceding context, for the field label
}
export interface HighlightedLine {
  kind: 'highlighted';
  startIdx: number;
  endIdx: number;
  text: string;
  highlights: Highlight[];
}

// A whole keypoint (🥖) category block — one form, many rows (see keypointBlock).
export interface KeypointBlockLine {
  kind: 'keypoint_block';
  startIdx: number;
  endIdx: number;
  block: KeypointBlock;
}

// A queue (⚪) row with a modelled template form (see typedLines).
export interface TypedLine {
  kind: 'typed';
  typedKind: TypedKind;
  startIdx: number;
  endIdx: number;
  fields: TypedFields;
  meta: TypedMeta;
}

// ── 2026-09-03 tree/branch format (see treeBlock.ts) ──────────────────────
export interface CategoryTreeLine {
  kind: 'category_tree';
  startIdx: number;
  endIdx: number;
  fields: CategoryTreeFields;
}
export interface LongestQueueLine {
  kind: 'longest_queue';
  startIdx: number;
  endIdx: number;
  fields: LongestQueueFields;
}
export interface LongQueueLine {
  kind: 'long_queue';
  startIdx: number;
  endIdx: number;
  fields: LongQueueFields;
}
export interface NoVisitorsLine {
  kind: 'no_visitors';
  startIdx: number;
  endIdx: number;
  fields: NoVisitorsFields;
}
// 2026-09-04: the "Чеклист" section (checklistBlock.ts) — flat, no ├/└
// branches, so its own line kind rather than reusing the tree-block shapes.
export interface ChecklistLine {
  kind: 'checklist';
  startIdx: number;
  endIdx: number;
  fields: ChecklistFields;
}

export interface OtherLine {
  kind: 'other';
  startIdx: number;
  endIdx: number;
  text: string; // the raw row (may contain inline html)
  isDivider: boolean; // ━ section title — not form-editable
  isBlank: boolean;
}

export type LogicalLine =
  | FoodStatusLine
  | KeypointBlockLine
  | TypedLine
  | CategoryTreeLine
  | LongestQueueLine
  | LongQueueLine
  | NoVisitorsLine
  | ChecklistLine
  | HighlightedLine
  | OtherLine;

/** Whether `bodyHtml` is the 2026-09-03 tree/branch format (bare `<b>`
 *  section headers, no `━` dividers) vs a legacy sentence-based format
 *  (either of the two prior variants keypointBlock.ts calls OLD/NEW — both
 *  superseded by today's rewrite). `parseBody`'s tree-line recognisers
 *  (category_tree/longest_queue/long_queue/no_visitors) only ever match
 *  text this rewrite emits, so callers gate structured editing on this
 *  first: ConstructorView/InteractiveView render a legacy body read-only
 *  (Alexey: old formats become view-only) instead of running it through
 *  per-line forms/charts. Bias toward 'false' (legacy/read-only) when
 *  ambiguous — a mis-detected legacy report losing structured editing is a
 *  far smaller problem than a mis-detected new report getting edited by
 *  forms that don't actually understand its text. */
export function isTreeFormat(bodyHtml: string): boolean {
  if (bodyHtml.includes('━')) return false;
  // "Чеклист" (2026-09-04) included so a report where empty_shelf/
  // checkout_control produced nothing but Чеклист did still gets detected
  // as the new format — see build_checklist_report_section.
  return /<b>\s*(Выкладка|Очередь и бар|Чек-?лист)\s*<\/b>/u.test(bodyHtml);
}

const SPAN_RE = /<(b|u)>([\s\S]*?)<\/\1>/gi;

/** Extract <b>/<u> highlighted values from a row, each with a short context label. */
export function extractHighlights(row: string): Highlight[] {
  const out: Highlight[] = [];
  let m: RegExpExecArray | null;
  SPAN_RE.lastIndex = 0;
  while ((m = SPAN_RE.exec(row)) !== null) {
    const before = row
      .slice(0, m.index)
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    out.push({ tag: m[1].toLowerCase() as 'b' | 'u', inner: m[2], label: before.slice(-32) });
  }
  return out;
}

/** Rebuild a row by substituting new inner text into its <b>/<u> spans, in order. */
export function applyHighlights(row: string, inners: string[]): string {
  let i = 0;
  return row.replace(SPAN_RE, (_full, tag: string) => `<${tag}>${inners[i++] ?? ''}</${tag}>`);
}

const FOOD_OK_RE = /^\s*🟢\s+(.+?):\s*не было\s*$/u;
const FOOD_PROBLEM_RE =
  /^\s*(🟡|🟠|🔴|⚪|⚫)\s+(.+?):\s*по\s+<u>\s*(\d+)\s+(?:полке|полкам)\s*<\/u>\s+средняя нехватка\s+<u>\s*(\d+)%\.?\s*<\/u>\s+Проблемы наблюдались в течение\s+<u>\s*([\d.]+)\s*ч\s*<\/u>\.?\s*$/u;
const BLOCKQUOTE_LINKS_RE =
  /^\s*<blockquote>\s*Ссылки на проблемные зоны:\s*(.*?)\s*<\/blockquote>\s*$/u;
const ANCHOR_RE = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/giu;

function parseLinks(inner: string): ProblemLink[] {
  const links: ProblemLink[] = [];
  let rest = inner;
  let m: RegExpExecArray | null;
  ANCHOR_RE.lastIndex = 0;
  while ((m = ANCHOR_RE.exec(inner)) !== null) {
    links.push({ name: m[2].trim(), url: m[1] });
  }
  if (links.length > 0) return links;
  // No anchors — treat as a plain comma-separated name list (BE fallback path).
  rest = inner.replace(/<[^>]+>/g, '').trim();
  if (!rest) return [];
  return rest
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((name) => ({ name, url: null }));
}

/** Parse a full body_html into logical lines. `openTime` (HH:MM МСК, from working
 * hours) lets keypoint blocks recompute "после открытия". */
export function parseBody(bodyHtml: string, openTime: string | null = null): LogicalLine[] {
  const rows = bodyHtml.split('\n');
  const out: LogicalLine[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    const okM = FOOD_OK_RE.exec(row);
    if (okM) {
      out.push({
        kind: 'food_status',
        startIdx: i,
        endIdx: i,
        fields: {
          rackType: okM[1].trim(),
          ok: true,
          severity: 'yellow',
          shelfCount: 1,
          avgShortage: 0,
          durationHours: 0,
          links: [],
        },
      });
      continue;
    }

    const probM = FOOD_PROBLEM_RE.exec(row);
    if (probM) {
      let endIdx = i;
      let links: ProblemLink[] = [];
      const bqM = i + 1 < rows.length ? BLOCKQUOTE_LINKS_RE.exec(rows[i + 1]) : null;
      if (bqM) {
        endIdx = i + 1;
        links = parseLinks(bqM[1]);
      }
      out.push({
        kind: 'food_status',
        startIdx: i,
        endIdx,
        fields: {
          rackType: probM[2].trim(),
          ok: false,
          severity: EMOJI_SEVERITY[probM[1]] ?? 'yellow',
          shelfCount: Number(probM[3]),
          avgShortage: Number(probM[4]),
          durationHours: Number(probM[5]),
          links,
        },
      });
      i = endIdx;
      continue;
    }

    const isDivider = row.includes('━');

    // ── 2026-09-03 tree/branch format (treeBlock.ts) — checked before the
    // legacy OLD-format recognisers below (their text shapes never collide
    // with these, so that ordering doesn't matter). WITHIN this block order
    // DOES matter: isCategoryTreeRow's header regex is `{5-emoji} {rest}`
    // with no further constraint on `rest`, so it would also match
    // "⚪ Без посетителей..." / "🟢 Очередь от N человек..." rows if checked
    // first — the more specific single-line recognisers must run first. ──
    if (!isDivider && isLongQueueRow(row)) {
      // "└ Вторая касса была открыта: N минут" (2026-09-08) belongs to this block.
      const withBranch = isLongQueueSecondCashierRow(rows[i + 1]);
      const end = withBranch ? i + 1 : i;
      out.push({
        kind: 'long_queue',
        startIdx: i,
        endIdx: end,
        fields: parseLongQueueFields(row, withBranch ? rows[i + 1] : undefined),
      });
      i = end;
      continue;
    }
    if (!isDivider && isNoVisitorsRow(row)) {
      out.push({ kind: 'no_visitors', startIdx: i, endIdx: i, fields: parseNoVisitorsFields(row) });
      continue;
    }
    if (!isDivider && isLongestQueueRow(row, rows[i + 1])) {
      const end = consumeTreeBlockRows(rows, i);
      out.push({
        kind: 'longest_queue',
        startIdx: i,
        endIdx: end,
        fields: parseLongestQueueFields(rows.slice(i, end + 1)),
      });
      i = end;
      continue;
    }
    if (!isDivider && isCategoryTreeRow(row, rows[i + 1])) {
      const end = consumeTreeBlockRows(rows, i);
      out.push({
        kind: 'category_tree',
        startIdx: i,
        endIdx: end,
        fields: parseCategoryTreeFields(rows.slice(i, end + 1)),
      });
      i = end;
      continue;
    }
    if (!isDivider && isChecklistRow(row, rows[i + 1])) {
      const end = consumeChecklistBlockRows(rows, i);
      out.push({
        kind: 'checklist',
        startIdx: i,
        endIdx: end,
        fields: parseChecklistFields(rows.slice(i, end + 1)),
      });
      i = end;
      continue;
    }

    // Keypoint category block — consume consecutive same-emoji keypoint rows.
    if (!isDivider && isKeypointRow(row)) {
      const emoji = keypointEmoji(row);
      let end = i;
      if (emoji !== null) {
        while (end + 1 < rows.length && keypointEmoji(rows[end + 1]) === emoji && isKeypointRow(rows[end + 1])) {
          end++;
        }
      }
      out.push({
        kind: 'keypoint_block',
        startIdx: i,
        endIdx: end,
        block: parseKeypointBlock(rows.slice(i, end + 1), openTime),
      });
      i = end;
      continue;
    }

    const typed = isDivider ? null : matchTypedLine(row);
    if (typed) {
      out.push({ kind: 'typed', startIdx: i, endIdx: i, ...typed });
      continue;
    }

    const highlights = isDivider ? [] : extractHighlights(row);
    if (highlights.length > 0) {
      out.push({ kind: 'highlighted', startIdx: i, endIdx: i, text: row, highlights });
      continue;
    }

    out.push({
      kind: 'other',
      startIdx: i,
      endIdx: i,
      text: row,
      isDivider,
      isBlank: row.trim() === '',
    });
  }
  return out;
}

// ── Sections (D3 include/exclude) ────────────────────────────────────────────

/** Plain label of a ━ divider row (its section key). */
export function dividerLabel(row: string): string {
  return row.replace(/<[^>]+>/g, '').replace(/━/g, '').trim();
}

/** Drop hidden sections (divider + its lines up to the next divider) from body_html. */
export function stripHiddenSections(bodyHtml: string, hidden: Set<string>): string {
  if (hidden.size === 0) return bodyHtml;
  const rows = bodyHtml.split('\n');
  const out: string[] = [];
  let curKey = '';
  let curHidden = false;
  for (const row of rows) {
    if (row.includes('━')) {
      curKey = dividerLabel(row);
      curHidden = hidden.has(curKey);
      if (curHidden) continue; // drop the divider too
      out.push(row);
      continue;
    }
    if (curHidden) continue;
    out.push(row);
  }
  return out.join('\n');
}

/** Drop removed rows/blocks from body_html. `hidden` holds each removed
 *  block as its rows joined by '\n' (a text key, so it survives the index
 *  shifts other edits cause). A blank row directly after a removed block is
 *  dropped too when the block was also preceded by a blank, so the
 *  Telegram copy doesn't get a double empty line where the block was. */
export function stripHiddenRows(bodyHtml: string, hidden: Set<string>): string {
  if (hidden.size === 0) return bodyHtml;
  const blocks = Array.from(hidden, (k) => k.split('\n'));
  const rows = bodyHtml.split('\n');
  const out: string[] = [];
  let i = 0;
  while (i < rows.length) {
    const block = blocks.find((b) => b.every((r, j) => rows[i + j] === r));
    if (!block) {
      out.push(rows[i]);
      i += 1;
      continue;
    }
    i += block.length;
    const prevBlank = out.length === 0 || (out[out.length - 1] ?? '').trim() === '';
    if (prevBlank && i < rows.length && rows[i].trim() === '') i += 1;
  }
  return out.join('\n');
}

// ── Grafana problem-link time range (#3) ─────────────────────────────────────
// Links look like `{base}/d/{id}/vykladka?var-racks_name={n}&from={ms}&to={ms}`.
// The per-rack dashboard id is already baked in, so we reconstruct "the link for a
// new time range" by rewriting from/to — no BE call needed for racks already linked.

export function getLinkTimeRange(url: string): { fromMs: number | null; toMs: number | null } {
  try {
    const u = new URL(url);
    const n = (v: string | null) => (v && Number.isFinite(Number(v)) ? Number(v) : null);
    return { fromMs: n(u.searchParams.get('from')), toMs: n(u.searchParams.get('to')) };
  } catch {
    return { fromMs: null, toMs: null };
  }
}

export function setLinkTimeRange(url: string, fromMs: number, toMs: number): string {
  try {
    const u = new URL(url);
    u.searchParams.set('from', String(fromMs));
    u.searchParams.set('to', String(toMs));
    return u.toString();
  } catch {
    return url;
  }
}

// Reports are МСК (UTC+3). Convert a day + HH:MM МСК ↔ the epoch-ms Grafana wants.
const MSK_OFFSET_MS = 3 * 60 * 60 * 1000;

export function mskHhmmToMs(day: string, hhmm: string): number | null {
  const [y, mo, d] = day.split('-').map(Number);
  const [hh, mm] = hhmm.split(':').map(Number);
  if (!y || !mo || !d || Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return Date.UTC(y, mo - 1, d, hh, mm) - MSK_OFFSET_MS;
}

export function msToMskHhmm(ms: number): string {
  const d = new Date(ms + MSK_OFFSET_MS);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

// Mirrors empty_shelf.py `overall_performance` (the flat pre-tree "Выкладка"
// status line, unchanged since 2026-02): avg shortage ≥ 40% → 🔴 when the
// problem lasted ≥ 10 h, else 🟠; under 40% → 🟡. Backend compares the raw
// float; the constructor only sees the rounded «N%» / «H.Hч» the row prints,
// so a row printing exactly «40%» derives 🟠 even when the backend's 39.6
// was 🟡 — the one unavoidable boundary artefact.
const FOOD_SHORTAGE_HIGH_AT = 40;
const FOOD_RED_HOURS_AT = 10;

export function deriveFoodSeverity(avgShortage: number, durationHours: number): Severity {
  if (avgShortage >= FOOD_SHORTAGE_HIGH_AT) return durationHours >= FOOD_RED_HOURS_AT ? 'red' : 'orange';
  return 'yellow';
}

/** Serialise a food_status entry back to its raw row(s). The circle is always
 *  derived from avgShortage/durationHours — a stored `severity` (incl. a
 *  legacy ⚪ grey) is ignored on write. */
export function serializeFoodStatus(f: FoodStatusFields): string[] {
  if (f.ok) return [`🟢 ${f.rackType}: не было`];

  const shelfWord = f.shelfCount === 1 ? 'полке' : 'полкам';
  const emoji = SEVERITY_EMOJI[deriveFoodSeverity(f.avgShortage, f.durationHours)];
  const statusRow =
    `${emoji} ${f.rackType}: по <u>${f.shelfCount} ${shelfWord}</u> ` +
    `средняя нехватка <u>${Math.round(f.avgShortage)}%.</u> ` +
    `Проблемы наблюдались в течение <u>${f.durationHours.toFixed(1)}ч</u>.`;

  const rows = [statusRow];
  if (f.links.length > 0) {
    const rendered = f.links
      .map((l) => (l.url ? `<a href="${l.url}">${l.name}</a>` : l.name))
      .join(', ');
    rows.push(`<blockquote>Ссылки на проблемные зоны: ${rendered}</blockquote>`);
  }
  return rows;
}

/** Replace a logical line's raw span in body_html with new rows. */
export function spliceLine(bodyHtml: string, line: LogicalLine, newRows: string[]): string {
  const rows = bodyHtml.split('\n');
  rows.splice(line.startIdx, line.endIdx - line.startIdx + 1, ...newRows);
  return rows.join('\n');
}
